import { useAppLayout } from "@app/components/sparkle/AppLayoutContext";
import { useFeatureFlags } from "@app/lib/auth/AuthContext";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  NavigationListCompactLabel,
  NavigationListItem,
  Settings01,
  XClose,
} from "@dust-tt/sparkle";
import type { ReactNode } from "react";

interface SettingsModalProps {
  children: ReactNode;
  onClose: () => void;
}

export function SettingsModal({ children, onClose }: SettingsModalProps) {
  const { navChildren, subNavigation } = useAppLayout();
  const { hasFeature } = useFeatureFlags();

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        overlayClassName="bg-black/40 backdrop-blur-md dark:bg-black/60"
        className="flex h-[min(820px,calc(100vh-2rem))] w-[min(1120px,calc(100vw-2rem))] max-w-[min(1120px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border-stone-200 bg-muted-background dark:border-stone-700 p-0 shadow-2xl sm:max-w-[min(1120px,calc(100vw-2rem))]"
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-stone-200 dark:border-stone-700 px-5 py-3.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
            <Settings01 className="h-4.5 w-4.5" />
          </span>
          <div className="min-w-0 flex-1">
            <DialogTitle className="truncate">Settings</DialogTitle>
            <p className="truncate text-xs text-muted-foreground">
              Your account, workspace, and connections
            </p>
          </div>
          <DialogClose asChild>
            <Button
              icon={XClose}
              variant="ghost"
              size="sm"
              aria-label="Close settings"
              className="rounded-lg"
            />
          </DialogClose>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3 p-3 sm:flex-row">
          <nav
            aria-label="Settings"
            className="flex max-h-52 shrink-0 flex-col gap-3 overflow-y-auto sm:max-h-none sm:w-60"
          >
            {subNavigation?.map((nav) => (
              <section key={`settings-nav-${nav.id}`} className="flex flex-col">
                {nav.label && <NavigationListCompactLabel label={nav.label} />}
                <div className="flex flex-col gap-0.5 rounded-xl border border-stone-200 bg-background dark:border-stone-700 p-1 shadow-2xs">
                  {nav.menus
                    .filter(
                      (menu) =>
                        !menu.featureFlag || hasFeature(menu.featureFlag)
                    )
                    .map((menu) => (
                      <NavigationListItem
                        key={menu.id}
                        selected={menu.current}
                        disabled={menu.disabled}
                        label={menu.label}
                        icon={menu.icon}
                        href={menu.href}
                        target={menu.target}
                        className="rounded-lg"
                      />
                    ))}
                </div>
              </section>
            ))}
            {navChildren}
          </nav>
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto rounded-xl border border-stone-200 bg-background dark:border-stone-700 shadow-2xs">
            {children}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
